import { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Lock, Trash } from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

const baseUrl = import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1/";

const hasOwnerRights = (user) => {
    if (!user?.role?.permissions) return false;
    if (user.role?.name?.toLowerCase?.().includes("admin")) return true;
    let perms = user.role.permissions;
    if (typeof perms === "string") { try { perms = JSON.parse(perms); } catch { return false; } }
    return Array.isArray(perms) && perms.includes("Settings");
};

/**
 * Owner/superadmin tool to hold back ("reserve") stock at a shop so it isn't
 * sold. Available-to-sell at that shop = on-hand − active reservations. Hidden
 * for users without owner rights.
 */
export default function ProductReservations({ productId }) {
    const { toast } = useToast();
    const user = useSelector((state) => state.auth.user);
    const reduxShops = useSelector((state) => state.shop?.shops);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ shop_id: "", quantity: "", reason: "" });
    const [saving, setSaving] = useState(false);
    const [localShops, setLocalShops] = useState([]);

    const canManage = hasOwnerRights(user);
    const shops = (reduxShops && reduxShops.length ? reduxShops : localShops) || [];

    // Fetch shops directly if the store doesn't already have them.
    useEffect(() => {
        if (!canManage) return;
        if (reduxShops && reduxShops.length) return;
        axios.get(`${baseUrl}shops`, { withCredentials: true })
            .then((res) => { if (res.data.success) setLocalShops(res.data.data || []); })
            .catch(() => { /* ignore */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canManage]);

    const load = async () => {
        try {
            const res = await axios.get(`${baseUrl}reservations`, {
                params: { product_id: productId }, withCredentials: true,
            });
            if (res.data.success) setRows(res.data.data || []);
        } catch { /* ignore */ } finally { setLoading(false); }
    };

    useEffect(() => {
        if (productId) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId]);

    if (!productId || !canManage) return null;

    const add = async () => {
        if (!form.shop_id || !(parseInt(form.quantity) > 0)) {
            toast({ variant: "destructive", title: "Missing info", description: "Pick a shop and a quantity greater than 0." });
            return;
        }
        setSaving(true);
        try {
            await axios.post(`${baseUrl}reservations`, {
                product_id: productId, shop_id: parseInt(form.shop_id),
                quantity: parseInt(form.quantity), reason: form.reason || null,
            }, { withCredentials: true });
            toast({ title: "Reserved", description: "Stock held back from sale.", className: "bg-green-50 dark:bg-green-900/30 border-green-200" });
            setForm({ shop_id: "", quantity: "", reason: "" });
            load();
        } catch (e) {
            toast({ variant: "destructive", title: "Error", description: e.response?.data?.message || "Failed to reserve" });
        } finally { setSaving(false); }
    };

    const release = async (id) => {
        try {
            await axios.post(`${baseUrl}reservations/${id}/release`, {}, { withCredentials: true });
            toast({ title: "Released", description: "Stock is available for sale again." });
            load();
        } catch (e) {
            toast({ variant: "destructive", title: "Error", description: e.response?.data?.message || "Failed to release" });
        }
    };

    const shopName = (id) => shops?.find((s) => String(s.id) === String(id))?.name || `Shop ${id}`;

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                    <Lock className="h-4 w-4" /> Reserved Stock
                </CardTitle>
                <CardDescription>
                    Hold back stock at a shop so it can't be sold (e.g. set aside for an order). Available-to-sell
                    at that shop = on-hand − reserved.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {loading ? (
                    <div className="flex items-center justify-center h-16"><Loader2 className="h-5 w-5 animate-spin" /></div>
                ) : (
                    <>
                        {rows.length > 0 && (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Shop</TableHead>
                                        <TableHead className="text-right">Qty reserved</TableHead>
                                        <TableHead>Reason</TableHead>
                                        <TableHead className="w-12"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {rows.map((r) => (
                                        <TableRow key={r.id}>
                                            <TableCell>{r.Shop?.name || shopName(r.shop_id)}</TableCell>
                                            <TableCell className="text-right font-medium">{r.quantity}</TableCell>
                                            <TableCell className="text-muted-foreground">{r.reason || "—"}</TableCell>
                                            <TableCell>
                                                <Button type="button" variant="ghost" size="icon" onClick={() => release(r.id)} title="Release">
                                                    <Trash className="h-4 w-4 text-red-500" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
                            <div className="sm:col-span-1">
                                <Select value={form.shop_id} onValueChange={(v) => setForm({ ...form, shop_id: v })}>
                                    <SelectTrigger><SelectValue placeholder="Shop" /></SelectTrigger>
                                    <SelectContent>
                                        {shops?.map((s) => (
                                            <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <Input type="number" min="1" placeholder="Qty" value={form.quantity}
                                onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
                            <Input placeholder="Reason (optional)" value={form.reason}
                                onChange={(e) => setForm({ ...form, reason: e.target.value })} />
                            <Button type="button" onClick={add} disabled={saving}>
                                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                                Reserve
                            </Button>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}
